// Seasonal/holiday occasions that appear first in the quiz when active.
// Priority: lower number = higher priority when multiple events overlap.
// Countries: null = worldwide, or array of ISO 3166-1 alpha-2 country codes.
//
// ONLY events where people actively buy gifts for others.
// No celebration-only holidays (4th of July, Karneval, etc.)
//
// Islamic holidays (Eid, Ramadan) and Jewish holidays shift on the
// Gregorian calendar each year. Hindu holidays (Diwali, Holi, Rakhi) also
// shift based on lunar calendar. The date windows below use generous ranges
// that cover the typical span across years. Review annually for accuracy.
//
// Last reviewed: April 2026
// Top 5 DAU countries: IL (90), US (28), EG (16), IN (15), DE (4)

export const SEASONAL_OCCASIONS = [

  // ══════════════════════════════════════════════════════════════
  // WORLDWIDE — Show to all countries
  // ══════════════════════════════════════════════════════════════
  {
    value: 'newyears',
    label: "New Year's",
    emoji: '\u{1F389}',
    startMonth: 12,
    startDay: 20,
    endMonth: 1,
    endDay: 5,
    countries: null,
    priority: 1
  },
  {
    value: 'valentines',
    label: "Valentine's Day",
    emoji: '\u{1F498}',
    startMonth: 1,
    startDay: 14,
    endMonth: 2,
    endDay: 14,
    countries: null,
    priority: 1
  },
  {
    value: 'christmas',
    label: 'Christmas',
    emoji: '\u{1F385}',
    startMonth: 11,
    startDay: 15,
    endMonth: 12,
    endDay: 25,
    countries: null,
    priority: 1
  },
  {
    value: 'womensday',
    label: "Women's Day",
    emoji: '\u{1F338}',
    startMonth: 2,
    startDay: 22,
    endMonth: 3,
    endDay: 8,
    countries: null, // Worldwide, but MASSIVE in Russia/CIS — bigger than Valentine's there
    priority: 2
  },

  // ══════════════════════════════════════════════════════════════
  // SHOPPING EVENTS — Deal-focused gift buying
  // ══════════════════════════════════════════════════════════════
  {
    value: 'blackfriday',
    label: 'Black Friday',
    emoji: '\u{1F6D2}',
    startMonth: 11,
    startDay: 20,
    endMonth: 12,
    endDay: 2,
    countries: ['US', 'CA', 'GB', 'DE', 'FR', 'AU', 'IL', 'IN', 'EG'],
    priority: 1
  },
  {
    value: 'primeday',
    label: 'Prime Day Deals',
    emoji: '\u{2B50}',
    startMonth: 7,
    startDay: 8,
    endMonth: 7,
    endDay: 20,
    countries: ['US', 'CA', 'GB', 'DE', 'FR', 'AU', 'IN'],
    priority: 2
  },

  // ══════════════════════════════════════════════════════════════
  // ISRAEL (IL) — #1 DAU country
  // ══════════════════════════════════════════════════════════════
  {
    value: 'israelimothersday',
    label: "Mother's Day",
    emoji: '\u{1F490}',
    startMonth: 1,
    startDay: 6,
    endMonth: 2,
    endDay: 20,
    countries: ['IL'],
    priority: 2
  },
  {
    value: 'purim',
    label: 'Purim',
    emoji: '\u{1F3AD}',
    startMonth: 2,
    startDay: 1,
    endMonth: 3,
    endDay: 26,
    countries: ['IL', 'US'],
    priority: 2
  },
  {
    value: 'passover',
    label: 'Passover',
    emoji: '\u{1F377}',
    startMonth: 3,
    startDay: 5,
    endMonth: 4,
    endDay: 12,
    countries: ['IL', 'US'],
    priority: 2
  },
  {
    value: 'shavuot',
    label: 'Shavuot',
    emoji: '\u{1F33E}',
    startMonth: 5,
    startDay: 15,
    endMonth: 6,
    endDay: 15,
    countries: ['IL', 'US'],
    priority: 3
  },
  {
    value: 'tubav',
    label: "Tu B'Av",
    emoji: '\u{2764}\u{FE0F}',
    startMonth: 7,
    startDay: 20,
    endMonth: 8,
    endDay: 15,
    countries: ['IL'],
    priority: 2
  },
  {
    value: 'roshhashanah',
    label: 'Rosh Hashanah',
    emoji: '\u{1F34F}',
    startMonth: 8,
    startDay: 15,
    endMonth: 10,
    endDay: 5,
    countries: ['IL', 'US'],
    priority: 2
  },
  {
    value: 'sukkot',
    label: 'Sukkot',
    emoji: '\u{1F343}',
    startMonth: 9,
    startDay: 20,
    endMonth: 10,
    endDay: 15,
    countries: ['IL', 'US'],
    priority: 3
  },
  {
    value: 'hanukkah',
    label: 'Hanukkah',
    emoji: '\u{1F54E}',
    startMonth: 11,
    startDay: 20,
    endMonth: 12,
    endDay: 31,
    countries: ['US', 'IL', 'CA', 'GB', 'FR'],
    priority: 2
  },
  {
    value: 'israelifathersday',
    label: "Father's Day",
    emoji: '\u{1F468}',
    startMonth: 11,
    startDay: 10,
    endMonth: 12,
    endDay: 5,
    countries: ['IL'],
    priority: 2
  },

  // ══════════════════════════════════════════════════════════════
  // UNITED STATES (US) — #2 DAU country
  // ══════════════════════════════════════════════════════════════
  {
    value: 'easter',
    label: 'Easter',
    emoji: '\u{1F430}',
    startMonth: 3,
    startDay: 8,
    endMonth: 4,
    endDay: 8,
    countries: ['US', 'CA', 'GB', 'AU', 'DE', 'FR'],
    priority: 2
  },
  {
    value: 'mothersday',
    label: "Mother's Day",
    emoji: '\u{1F469}\u{200D}\u{2764}\u{FE0F}',
    startMonth: 4,
    startDay: 10,
    endMonth: 5,
    endDay: 12,
    countries: ['US', 'CA', 'AU', 'NZ', 'IN', 'DE', 'IT', 'JP', 'BR'],
    priority: 2
  },
  {
    value: 'graduation',
    label: 'Graduation',
    emoji: '\u{1F393}',
    startMonth: 5,
    startDay: 1,
    endMonth: 6,
    endDay: 15,
    countries: ['US', 'CA', 'IN'],
    priority: 2
  },
  {
    value: 'fathersday',
    label: "Father's Day",
    emoji: '\u{1F468}\u{200D}\u{2764}\u{FE0F}',
    startMonth: 5,
    startDay: 18,
    endMonth: 6,
    endDay: 16,
    countries: ['US', 'CA', 'GB', 'AU', 'NZ', 'IN', 'FR'],
    priority: 2
  },
  {
    value: 'halloween',
    label: 'Halloween',
    emoji: '\u{1F383}',
    startMonth: 10,
    startDay: 1,
    endMonth: 10,
    endDay: 31,
    countries: ['US', 'CA', 'GB', 'AU', 'DE'],
    priority: 2
  },
  {
    value: 'thanksgiving',
    label: 'Thanksgiving',
    emoji: '\u{1F983}',
    startMonth: 11,
    startDay: 1,
    endMonth: 11,
    endDay: 28,
    countries: ['US', 'CA'],
    priority: 2
  },
  {
    value: 'backtoschool',
    label: 'Back to School',
    emoji: '\u{1F392}',
    startMonth: 7,
    startDay: 15,
    endMonth: 9,
    endDay: 10,
    countries: ['US', 'CA', 'GB', 'AU', 'IL', 'DE', 'IN', 'EG'],
    priority: 3
  },
  {
    value: 'lunarnewyear',
    label: 'Lunar New Year',
    emoji: '\u{1F9E7}',
    startMonth: 1,
    startDay: 10,
    endMonth: 2,
    endDay: 20,
    countries: ['US', 'CA', 'AU', 'GB', 'SG', 'MY'],
    priority: 3
  },
  {
    value: 'galentines',
    label: "Galentine's Day",
    emoji: '\u{1F46D}',
    startMonth: 2,
    startDay: 5,
    endMonth: 2,
    endDay: 13,
    countries: ['US', 'CA', 'GB', 'AU'],
    priority: 3
  },
  {
    value: 'teacherday_us',
    label: "Teacher's Day",
    emoji: '\u{1F34E}',
    startMonth: 4,
    startDay: 28,
    endMonth: 5,
    endDay: 10,
    countries: ['US'],
    priority: 3
  },
  {
    value: 'grandparentsday',
    label: "Grandparents' Day",
    emoji: '\u{1F475}',
    startMonth: 8,
    startDay: 25,
    endMonth: 9,
    endDay: 15,
    countries: ['US', 'CA', 'GB', 'AU'],
    priority: 3
  },

  // ══════════════════════════════════════════════════════════════
  // ISLAMIC HOLIDAYS — Egypt (#3), India (#4), and worldwide
  // Dates shift ~11 days earlier each Gregorian year.
  // Windows below are generous to cover 2026-2028 range.
  // ══════════════════════════════════════════════════════════════
  {
    value: 'ramadan',
    label: 'Ramadan',
    emoji: '\u{1F319}',
    startMonth: 1,
    startDay: 20,
    endMonth: 3,
    endDay: 25,
    countries: ['EG', 'IN', 'AE', 'DE', 'FR', 'GB', 'US', 'MY', 'SG'],
    priority: 2
  },
  {
    value: 'eidfitr',
    label: 'Eid al-Fitr',
    emoji: '\u{1F319}',
    startMonth: 3,
    startDay: 5,
    endMonth: 4,
    endDay: 5,
    countries: ['EG', 'IN', 'AE', 'DE', 'FR', 'GB', 'US', 'MY', 'SG'],
    priority: 1
  },
  {
    value: 'eidadha',
    label: 'Eid al-Adha',
    emoji: '\u{1F54C}',
    startMonth: 5,
    startDay: 10,
    endMonth: 6,
    endDay: 10,
    countries: ['EG', 'IN', 'AE', 'DE', 'FR', 'GB', 'US', 'MY', 'SG'],
    priority: 1
  },

  // ══════════════════════════════════════════════════════════════
  // EGYPT (EG) — #3 DAU country
  // ══════════════════════════════════════════════════════════════
  {
    value: 'egyptianmothersday',
    label: "Mother's Day",
    emoji: '\u{1F490}',
    startMonth: 3,
    startDay: 7,
    endMonth: 3,
    endDay: 21,
    countries: ['EG'],
    priority: 1
  },
  {
    value: 'copticchristmas',
    label: 'Coptic Christmas',
    emoji: '\u{2721}',
    startMonth: 12,
    startDay: 28,
    endMonth: 1,
    endDay: 7,
    countries: ['EG'],
    priority: 2
  },

  // ══════════════════════════════════════════════════════════════
  // INDIA (IN) — #4 DAU country
  // ══════════════════════════════════════════════════════════════
  {
    value: 'holi',
    label: 'Holi',
    emoji: '\u{1F308}',
    startMonth: 2,
    startDay: 20,
    endMonth: 3,
    endDay: 20,
    countries: ['IN', 'US', 'GB'],
    priority: 2
  },
  {
    value: 'rakhi',
    label: 'Raksha Bandhan',
    emoji: '\u{1F49D}',
    startMonth: 7,
    startDay: 25,
    endMonth: 8,
    endDay: 20,
    countries: ['IN', 'US', 'GB', 'AU'],
    priority: 1
  },
  {
    value: 'teacherday_in',
    label: "Teacher's Day",
    emoji: '\u{1F34E}',
    startMonth: 8,
    startDay: 28,
    endMonth: 9,
    endDay: 5,
    countries: ['IN'],
    priority: 3
  },
  {
    value: 'navratri',
    label: 'Navratri / Dussehra',
    emoji: '\u{1F31F}',
    startMonth: 9,
    startDay: 15,
    endMonth: 10,
    endDay: 15,
    countries: ['IN', 'US', 'GB'],
    priority: 2
  },
  {
    value: 'karvachauth',
    label: 'Karva Chauth',
    emoji: '\u{1F319}',
    startMonth: 10,
    startDay: 10,
    endMonth: 10,
    endDay: 30,
    countries: ['IN'],
    priority: 2
  },
  {
    value: 'diwali',
    label: 'Diwali',
    emoji: '\u{1FA94}',
    startMonth: 10,
    startDay: 10,
    endMonth: 11,
    endDay: 10,
    countries: ['IN', 'US', 'GB', 'AU', 'SG', 'MY'],
    priority: 1
  },
  {
    value: 'bhaidooj',
    label: 'Bhai Dooj',
    emoji: '\u{1F46B}',
    startMonth: 10,
    startDay: 20,
    endMonth: 11,
    endDay: 15,
    countries: ['IN'],
    priority: 3
  },

  // ══════════════════════════════════════════════════════════════
  // GERMANY (DE) — #5 DAU country
  // ══════════════════════════════════════════════════════════════
  {
    value: 'nikolaus',
    label: 'Nikolaustag',
    emoji: '\u{1F385}',
    startMonth: 11,
    startDay: 25,
    endMonth: 12,
    endDay: 6,
    countries: ['DE'],
    priority: 3
  },
  {
    value: 'einschulung',
    label: 'Einschulung',
    emoji: '\u{1F3C6}',
    startMonth: 7,
    startDay: 20,
    endMonth: 9,
    endDay: 15,
    countries: ['DE'],
    priority: 3
  },

  // ══════════════════════════════════════════════════════════════
  // OTHER REGIONAL — Growing markets
  // ══════════════════════════════════════════════════════════════
  {
    value: 'nowruz',
    label: 'Nowruz (Persian New Year)',
    emoji: '\u{1F33A}',
    startMonth: 3,
    startDay: 10,
    endMonth: 3,
    endDay: 25,
    countries: ['US', 'CA', 'DE', 'GB', 'AE'],
    priority: 3
  },
  {
    value: 'boxingday',
    label: 'Boxing Day',
    emoji: '\u{1F381}',
    startMonth: 12,
    startDay: 26,
    endMonth: 12,
    endDay: 31,
    countries: ['GB', 'CA', 'AU', 'NZ'],
    priority: 3
  },
  {
    value: 'mothersday_uk',
    label: "Mother's Day",
    emoji: '\u{1F490}',
    startMonth: 3,
    startDay: 1,
    endMonth: 3,
    endDay: 30,
    countries: ['GB'],
    priority: 2
  },

  // ══════════════════════════════════════════════════════════════
  // GLOBAL SHOPPING EVENTS — High affiliate revenue potential
  // ══════════════════════════════════════════════════════════════
  {
    value: 'singlesday',
    label: "Singles' Day (11.11)",
    emoji: '\u{1F6D2}',
    startMonth: 11,
    startDay: 1,
    endMonth: 11,
    endDay: 12,
    countries: null,
    priority: 2
  },

  // ══════════════════════════════════════════════════════════════
  // LATIN AMERICA — Growing gift market
  // ══════════════════════════════════════════════════════════════
  {
    value: 'elbuenfin',
    label: 'El Buen Fin',
    emoji: '\u{1F6D2}',
    startMonth: 11,
    startDay: 15,
    endMonth: 11,
    endDay: 20,
    countries: ['MX'],
    priority: 1
  },
  {
    value: 'threekingsday',
    label: 'Three Kings\' Day',
    emoji: '\u{1F451}',
    startMonth: 12,
    startDay: 20,
    endMonth: 1,
    endDay: 6,
    countries: ['ES', 'MX', 'AR', 'CO', 'PR'],
    priority: 1
  },

  // ══════════════════════════════════════════════════════════════
  // JAPAN / KOREA — Unique gift culture
  // ══════════════════════════════════════════════════════════════
  {
    value: 'whiteday',
    label: 'White Day',
    emoji: '\u{1F381}',
    startMonth: 2,
    startDay: 28,
    endMonth: 3,
    endDay: 14,
    countries: ['JP', 'KR', 'US'],
    priority: 2
  },

  // ══════════════════════════════════════════════════════════════
  // TURKEY — Growing user potential
  // ══════════════════════════════════════════════════════════════
  {
    value: 'teacherday_tr',
    label: "Teacher's Day",
    emoji: '\u{1F34E}',
    startMonth: 11,
    startDay: 18,
    endMonth: 11,
    endDay: 24,
    countries: ['TR'],
    priority: 3
  }
];
